import React, { FC, useState } from "react";
import { GetStaticProps } from "next";
import { cmsPageProps } from "../lib/pageProps";
import { Trans, useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import {
  Close,
  Engineering,
  MenuBook,
  NavigateBefore,
  NavigateNext,
  School,
  WhatsApp,
} from "@mui/icons-material";
import { Card, Grid, IconButton, Modal, styled } from "@mui/material";
import BreadcrumbsComponent from "../components/_shared/Breadcrumbs";
import CustomButton from "../components/_shared/CustomButton";
import YoutubeEmbed from "../components/_shared/YoutubeEmbed";
import AppContainer from "../layouts/AppContainer";
import Meta from "../layouts/Meta";

const FORM_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSf0yoU7Tt-iHG_r2htdA9dNbUzUmRVjvrgbsaILELuzuWf4hA/viewform";
const WHATSAPP_URL = "https://www.wasap.my/+60149854804/Akademi%20Solar%20SOLS";
// Sampled from the original infographics so the rebuilt ones match.
const PATHWAY_COLORS = {
  green: "#3b7d4f",
  yellow: "#F9BE15",
  blue: "#26619D",
  orange: "#CC5802",
} as const;
const PROGRAMME_LOGO =
  "https://cdn.solssmart.org/static/impact/logos/sols-solar-academy.png";

const SolarAcademyNativePage: FC = () => {
  const { t } = useTranslation("solar-academy");
  const router = useRouter();
  const url = router.pathname;
  const brandName = t("title");
  const [activeActivityIndex, setActiveActivityIndex] = useState<number | null>(
    null,
  );

  const whyBenefits = [
    {
      key: "why-benefit1-h5",
      icon: "free-scholarship",
      alt: "Free scholarship icon",
    },
    {
      key: "why-benefit2-h5",
      icon: "accommodation-meals",
      alt: "Accommodation and meals icon",
    },
    {
      key: "why-benefit3-h5",
      icon: "industry-expert",
      alt: "Industry expert trainers icon",
    },
    {
      key: "why-benefit4-h5",
      icon: "qualification-certificate",
      alt: "Qualification certificates icon",
    },
    {
      key: "why-benefit5-h5",
      icon: "job-offer",
      alt: "Job offer icon",
    },
  ] as const;

  const subjectKeys = [
    "subjects-li1",
    "subjects-li2",
    "subjects-li3",
    "subjects-li4",
    "subjects-li5",
    "subjects-li6",
    "subjects-li7",
    "subjects-li8",
    "subjects-li9",
    "subjects-li10",
    "subjects-li11",
  ];

  const jobKeys = [
    "job-opportunities-li1",
    "job-opportunities-li2",
    "job-opportunities-li3",
    "job-opportunities-li4",
    "job-opportunities-li5",
    "job-opportunities-li6",
    "job-opportunities-li7",
  ];

  const criteriaKeys = ["criteria-li1", "criteria-li2", "criteria-li3"];

  // The programme plan and career path used to be Malay-only infographics shown
  // in every locale. As markup, every label is a translatable string, and a
  // figure like the allowance can change without a design request.
  const planPhases = [
    { key: "phase1", color: PATHWAY_COLORS.green, Icon: MenuBook },
    { key: "phase2", color: PATHWAY_COLORS.blue, Icon: Engineering },
    { key: "phase3", color: PATHWAY_COLORS.orange, Icon: School },
  ] as const;

  const careerSteps = [
    { key: "step1", color: PATHWAY_COLORS.green, textColor: "#FFFFFF" },
    { key: "step2", color: PATHWAY_COLORS.yellow, textColor: "#000000" },
    { key: "step3", color: PATHWAY_COLORS.blue, textColor: "#FFFFFF" },
    { key: "step4", color: PATHWAY_COLORS.orange, textColor: "#000000" },
  ] as const;

  // `image` names both the photo file and its alt key, so the two can't drift.
  const testimonials = [
    {
      nameKey: "testimony-1-name",
      quoteKey: "testimony-1-p",
      image: "azwanizuan-junior-wireman",
    },
    {
      nameKey: "testimony-2-name",
      quoteKey: "testimony-2-p",
      image: "haikal-fleet-manager",
    },
    {
      nameKey: "testimony-3-name",
      quoteKey: "testimony-3-p",
      image: "fatienismatulain-implementation-coordinator",
    },
    {
      nameKey: "testimony-4-name",
      quoteKey: "testimony-4-p",
      image: "ezni-operations-executive",
    },
  ] as const;

  const futureChecks = [
    "future-cta-check1",
    "future-cta-check2",
    "future-cta-check3",
  ];
  const ctaButtonSx = {
    "&.MuiButton-sizeMedium": { px: 4 }, // 32px L/R, beats the theme at equal specificity
    whiteSpace: "nowrap",
    width: "auto",
    maxWidth: "none",
  };

  const activities = [
    {
      src: "/assets/images/solar-academy/activity-students-computer-lab.webp",
      altKey: "alt-activity-students-computer-lab",
    },
    {
      src: "/assets/images/solar-academy/activity-instructor-whiteboard-class.webp",
      altKey: "alt-activity-instructor-whiteboard-class",
    },
    {
      src: "/assets/images/solar-academy/activity-students-table-discussion.webp",
      altKey: "alt-activity-students-table-discussion",
    },
    {
      src: "/assets/images/solar-academy/activity-students-dining-together.webp",
      altKey: "alt-activity-students-dining-together",
    },
    {
      src: "/assets/images/solar-academy/activity-rooftop-solar-panel-practical.webp",
      altKey: "alt-activity-rooftop-solar-panel-practical",
    },
    {
      src: "/assets/images/solar-academy/activity-students-green-classroom-lesson.webp",
      altKey: "alt-activity-students-green-classroom-lesson",
    },
    {
      src: "/assets/images/solar-academy/activity-sols-students-outdoor-event.webp",
      altKey: "alt-activity-sols-students-outdoor-event",
    },
    {
      src: "/assets/images/solar-academy/activity-students-group-photo-banner.webp",
      altKey: "alt-activity-students-group-photo-banner",
    },
    {
      src: "/assets/images/solar-academy/activity-student-ladder-rural-installation.webp",
      altKey: "alt-activity-student-ladder-rural-installation",
    },
    {
      src: "/assets/images/solar-academy/activity-mentor-laptop-session.webp",
      altKey: "alt-activity-mentor-laptop-session",
    },
    {
      src: "/assets/images/solar-academy/activity-student-installing-solar-panel.webp",
      altKey: "alt-activity-student-installing-solar-panel",
    },
    {
      src: "/assets/images/solar-academy/activity-laptop-training-session.webp",
      altKey: "alt-activity-laptop-training-session",
    },
    {
      src: "/assets/images/solar-academy/activity-students-roof-rail-mounting.webp",
      altKey: "alt-activity-students-roof-rail-mounting",
    },
    {
      src: "/assets/images/solar-academy/activity-student-presenting-cimb-event.webp",
      altKey: "alt-activity-student-presenting-cimb-event",
    },
    {
      src: "/assets/images/solar-academy/activity-students-sports-court.webp",
      altKey: "alt-activity-students-sports-court",
    },
    {
      src: "/assets/images/solar-academy/activity-students-group-sols-shirts.webp",
      altKey: "alt-activity-students-group-sols-shirts",
    },
  ] as const;
  const activityVideoIds = ["zcUCWQnIorU", "GOS4WE40G4I"] as const;

  const partnerLogos = [
    {
      src: "/assets/images/solar-academy/partner-logo-obama-foundation-scholars.webp",
      alt: "Obama Foundation Scholars logo",
    },
    {
      src: "/assets/images/solar-academy/partner-logo-hrd-corp.webp",
      alt: "HRD Corp logo",
    },
    {
      src: "/assets/images/solar-academy/partner-logo-google-for-education.webp",
      alt: "Google for Education logo",
    },
    {
      src: "/assets/images/solar-academy/partner-logo-jpk.webp",
      alt: "JPK (Department of Skills Development Malaysia) logo",
    },
    {
      src: "/assets/images/solar-academy/partner-logo-uob.webp",
      alt: "UOB logo",
    },
    {
      src: "/assets/images/solar-academy/partner-logo-jpmorgan-chase.webp",
      alt: "JPMorgan Chase logo",
    },
    {
      src: "/assets/images/solar-academy/partner-logo-kbs-ministry-youth-sports.webp",
      alt: "KBS (Ministry of Youth and Sports) logo",
    },
    {
      src: "/assets/images/solar-academy/partner-logo-rakan-muda.webp",
      alt: "Rakan Muda logo",
    },
    {
      src: "/assets/images/solar-academy/partner-logo-sols-energy.webp",
      alt: "SOLS Energy logo",
    },
    {
      src: "/assets/images/solar-academy/partner-logo-cimb-foundation.webp",
      alt: "CIMB and CIMB Foundation logos",
    },
  ] as const;

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "EducationalOccupationalProgram",
    name: t("title"),
    description: t("hero-p"),
    provider: {
      "@id": "https://sols.foundation/#organization",
    },
    programPrerequisites: criteriaKeys.map((key) => t(key)),
    timeToComplete: "P1Y",
    occupationalCategory: [
      "Renewable Energy",
      "Solar PV Installation",
      "Solar Installer",
    ],
    educationalCredentialAwarded: t("why-benefit4-h5"),
    offers: {
      "@type": "Offer",
      price: 0,
      priceCurrency: "MYR",
      availability: "https://schema.org/InStock",
    },
  };

  const schemaJson = JSON.stringify(schemaData).replace(/</g, "\\u003c");
  const openActivityLightbox = (index: number) => {
    setActiveActivityIndex(index);
  };
  const closeActivityLightbox = () => {
    setActiveActivityIndex(null);
  };
  const showPrevActivity = () => {
    setActiveActivityIndex((current) => {
      if (current === null) return current;
      return (current - 1 + activities.length) % activities.length;
    });
  };
  const showNextActivity = () => {
    setActiveActivityIndex((current) => {
      if (current === null) return current;
      return (current + 1) % activities.length;
    });
  };

  return (
    <AppContainer>
      <Meta
        title={t("title")}
        description={t("meta-description")}
        keywords={t("meta-keyword")}
      />
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger -- JSON-LD injection for SEO schema
        dangerouslySetInnerHTML={{ __html: schemaJson }}
      />

      <BreadcrumbsComponent url={url} />

      <Root>
        <section className="hero">
          <div className="heroGrid">
            <div className="heroText">
              <img
                src={PROGRAMME_LOGO}
                alt={`${brandName} programme logo`}
                className="programmeLogo"
              />
              <h1 className="heroTitle">
                {t("hero-h1-black1")}{" "}
                <span className="accentText">{t("hero-h1-yellow1")}</span>
              </h1>
              <p className="heroIntake">
                {[t("hero-h1-black2"), t("hero-h1-yellow2")].join(
                  // Chinese full-width punctuation already carries its own space.
                  /[。！？]$/.test(t("hero-h1-black2")) ? "" : " ",
                )}
              </p>
              <p>{t("hero-p")}</p>

              <div className="ctaRow">
                <a href={FORM_URL} target="_blank" rel="noopener noreferrer">
                  <CustomButton sx={ctaButtonSx}>
                    {t("hero-cta-apply")}
                  </CustomButton>
                </a>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="socmedIconHolder">
                    <WhatsApp className="socmedIcon" />
                  </span>
                </a>
              </div>
            </div>
            <div className="heroImageCol">
              <img
                src="/assets/images/solar-academy/hero-students-solar-panel-group.webp"
                alt={t("alt-hero-students-solar-panel-group")}
                className="heroImage"
              />
            </div>
          </div>
        </section>

        <section className="contentSection">
          <h2 style={{ textAlign: "center", padding: "1rem 0.5rem" }}>
            {t("why-h2-black")}{" "}
            <span className="accentText">{t("why-h2-yellow")}</span>
          </h2>
          <Grid container spacing={2} className="whyGrid">
            {whyBenefits.map((benefit) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                className="whyGridItem"
                key={benefit.key}
              >
                <Card className="whyCard">
                  <div className="benefitRow whyBenefitRow">
                    <img
                      src={`/assets/images/solar-academy/icon-${benefit.icon}.webp`}
                      alt={benefit.alt}
                    />
                    <h3>{t(benefit.key)}</h3>
                  </div>
                </Card>
              </Grid>
            ))}
          </Grid>
          <p style={{ textAlign: "center" }} className="urgencyText">
            {t("urgency-h4")}
          </p>
          <div className="ctaRow centerRow">
            <a href={FORM_URL} target="_blank" rel="noopener noreferrer">
              <CustomButton sx={ctaButtonSx}>
                {t("urgency-cta-apply")}
              </CustomButton>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              <span className="socmedIconHolder">
                <WhatsApp className="socmedIcon" />
              </span>
            </a>
          </div>
        </section>

        <section className="contentSection">
          <h2 style={{ textAlign: "center" }}>
            <span className="accentText">{t("activity-h2-yellow")}</span>{" "}
            {t("activity-h2-black")}
          </h2>
          <Grid container spacing={3} className="activityVideos">
            {activityVideoIds.map((videoId) => (
              <Grid item xs={12} md={6} key={videoId}>
                <YoutubeEmbed embedId={videoId} />
              </Grid>
            ))}
          </Grid>
          <Grid container spacing={1}>
            {activities.map((activity, index) => (
              <Grid item xs={6} sm={4} md={3} key={activity.src}>
                <button
                  type="button"
                  className="galleryButton"
                  onClick={() => openActivityLightbox(index)}
                  aria-label={t(activity.altKey)}
                >
                  <img
                    src={activity.src}
                    alt={t(activity.altKey)}
                    className="galleryImage"
                  />
                </button>
              </Grid>
            ))}
          </Grid>
          <StyledLightboxModal
            open={activeActivityIndex !== null}
            onClose={closeActivityLightbox}
            aria-labelledby="activity-lightbox-title"
          >
            <div className="lightboxOverlay" onClick={closeActivityLightbox}>
              <IconButton
                className="lightboxNav left"
                onClick={(event) => {
                  event.stopPropagation();
                  showPrevActivity();
                }}
                aria-label="Previous image"
              >
                <NavigateBefore />
              </IconButton>
              <div
                className="lightboxContent"
                onClick={(event) => event.stopPropagation()}
              >
                <IconButton
                  className="lightboxClose"
                  onClick={closeActivityLightbox}
                  aria-label="Close gallery"
                >
                  <Close />
                </IconButton>
                {activeActivityIndex !== null && (
                  <img
                    src={activities[activeActivityIndex].src}
                    alt={t(activities[activeActivityIndex].altKey)}
                    className="lightboxImage"
                  />
                )}
              </div>
              <IconButton
                className="lightboxNav right"
                onClick={(event) => {
                  event.stopPropagation();
                  showNextActivity();
                }}
                aria-label="Next image"
              >
                <NavigateNext />
              </IconButton>
            </div>
          </StyledLightboxModal>
          <div className="ctaRow centerRow" style={{ marginTop: "3rem" }}>
            <h3 style={{ textAlign: "center" }}>{t("become-student-h4")}</h3>
            <a href={FORM_URL} target="_blank" rel="noopener noreferrer">
              <CustomButton sx={ctaButtonSx}>
                {t("become-student-cta-apply")}
              </CustomButton>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              <span className="socmedIconHolder">
                <WhatsApp className="socmedIcon" />
              </span>
            </a>
          </div>
        </section>

        <section className="contentSection subjectsSection">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <img
                src="/assets/images/solar-academy/subjects-speaker-with-students.webp"
                alt={t("alt-subjects-speaker-with-students")}
                className="subjectsImage"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <h2>
                {t("subjects-h2-black")}{" "}
                <span className="accentText">{t("subjects-h2-yellow")}</span>
              </h2>
              <ul className="subjectsList">
                {subjectKeys.map((key) => (
                  <li key={key}>{t(key)}</li>
                ))}
              </ul>
              <div className="ctaRow">
                <a href={FORM_URL} target="_blank" rel="noopener noreferrer">
                  <CustomButton sx={ctaButtonSx}>
                    {t("subjects-cta-apply")}
                  </CustomButton>
                </a>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="socmedIconHolder">
                    <WhatsApp className="socmedIcon" />
                  </span>
                </a>
              </div>
            </Grid>
          </Grid>
        </section>

        <section className="contentSection">
          <h2 style={{ textAlign: "center" }}>
            <Trans
              t={t}
              i18nKey="program-plan-h2-order"
              values={{
                black: t("program-plan-h2-black"),
                yellow: t("program-plan-h2-yellow"),
              }}
              components={{
                black: <span />,
                yellow: <span className="accentText" />,
              }}
            />
          </h2>
          <p style={{ textAlign: "center" }}>{t("program-plan-p")}</p>
          <ol className="planPhases">
            {planPhases.map(({ key, color, Icon }, index) => (
              <li
                className="planPhase"
                key={key}
                style={{ "--phase-color": color } as React.CSSProperties}
              >
                <div className="planPin">
                  <span className="planPinInner">
                    <Icon />
                  </span>
                </div>
                <div className="planDuration">
                  {t(`program-plan-${key}-duration`)}
                  {index === planPhases.length - 1 && (
                    <svg
                      className="planArrow"
                      viewBox="0 0 40 72"
                      aria-hidden="true"
                    >
                      <polygon
                        points="4,4 36,36 4,68"
                        fill="currentColor"
                        stroke="currentColor"
                        strokeWidth="8"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <div className="planCard">
                  <h3>{t(`program-plan-${key}-title`)}</h3>
                  <p>{t(`program-plan-${key}-p`)}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="ctaRow centerRow">
            <a href={FORM_URL} target="_blank" rel="noopener noreferrer">
              <CustomButton sx={ctaButtonSx}>
                {t("program-plan-cta-apply")}
              </CustomButton>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              <span className="socmedIconHolder">
                <WhatsApp className="socmedIcon" />
              </span>
            </a>
          </div>
        </section>

        <section className="contentSection jobSection">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <h2>
                <span className="accentText">
                  {t("job-opportunities-h2-yellow")}
                </span>{" "}
                {t("job-opportunities-h2-black")}
              </h2>
              <ul className="jobList">
                {jobKeys.map((key) => (
                  <li key={key}>{t(key)}</li>
                ))}
              </ul>
              <div className="ctaRow">
                <a href={FORM_URL} target="_blank" rel="noopener noreferrer">
                  <CustomButton sx={ctaButtonSx}>
                    {t("job-opportunities-cta-apply")}
                  </CustomButton>
                </a>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="socmedIconHolder">
                    <WhatsApp className="socmedIcon" />
                  </span>
                </a>
              </div>
            </Grid>
            <Grid item xs={12} md={6}>
              <img
                src="/assets/images/solar-academy/job-opportunities-technicians-rooftop.webp"
                alt={t("alt-job-opportunities-technicians-rooftop")}
                className="jobImage"
              />
            </Grid>
          </Grid>
        </section>

        <section className="contentSection">
          <h2 style={{ textAlign: "center" }}>
            <span className="accentText">{t("career-path-h2-yellow")}</span>{" "}
            {t("career-path-h2-black")}
          </h2>
          <ol className="careerSteps">
            {careerSteps.map(({ key, color, textColor }, index) => (
              <li
                className="careerStep"
                key={key}
                style={{ backgroundColor: color, color: textColor }}
              >
                <h3>{t(`career-path-${key}-year`)}</h3>
                <p>{t(`career-path-${key}-role`)}</p>
                {index < careerSteps.length - 1 && (
                  <svg
                    className="careerArrow"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M14 100 C14 55 35 30 72 30"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="11"
                      strokeLinecap="round"
                      vectorEffect="non-scaling-stroke"
                    />
                    <polygon
                      points="72,12 96,30 72,48"
                      fill="currentColor"
                      stroke="currentColor"
                      strokeWidth="6"
                      strokeLinejoin="round"
                      vectorEffect="non-scaling-stroke"
                    />
                  </svg>
                )}
              </li>
            ))}
          </ol>
          <div className="ctaRow centerRow">
            <a href={FORM_URL} target="_blank" rel="noopener noreferrer">
              <CustomButton sx={ctaButtonSx}>
                {t("career-path-cta-apply")}
              </CustomButton>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              <span className="socmedIconHolder">
                <WhatsApp className="socmedIcon" />
              </span>
            </a>
          </div>
        </section>

        <section className="contentSection">
          <h2 style={{ textAlign: "center" }}>
            <Trans
              t={t}
              i18nKey="partners-h1-order"
              values={{
                black: t("partners-h1-black"),
                yellow: t("partners-h1-yellow"),
              }}
              components={{
                black: <span />,
                yellow: <span className="accentText" />,
              }}
            />
          </h2>
          <div className="partnersLogoGrid">
            {partnerLogos.map((logo) => (
              <div className="partnerLogoItem" key={logo.src}>
                <img src={logo.src} alt={logo.alt} className="partnerLogo" />
              </div>
            ))}
          </div>
        </section>

        <section className="contentSection criteriaSection">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <h2>
                <Trans
                  t={t}
                  i18nKey="criteria-h2-order"
                  values={{
                    black: t("criteria-h2-black"),
                    yellow: t("criteria-h2-yellow"),
                  }}
                  components={{
                    black: <span />,
                    yellow: <span className="accentText" />,
                  }}
                />
              </h2>
              <ul className="criteriaList">
                {criteriaKeys.map((key) => (
                  <li key={key}>{t(key)}</li>
                ))}
              </ul>
              <div className="ctaRow">
                <a href={FORM_URL} target="_blank" rel="noopener noreferrer">
                  <CustomButton sx={ctaButtonSx}>
                    {t("criteria-cta-apply")}
                  </CustomButton>
                </a>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="socmedIconHolder">
                    <WhatsApp className="socmedIcon" />
                  </span>
                </a>
              </div>
            </Grid>
            <Grid item xs={12} md={6}>
              <img
                src="/assets/images/solar-academy/criteria-students-smiling-classroom.webp"
                alt={t("alt-criteria-students-smiling-classroom")}
                className="criteriaImage"
              />
            </Grid>
          </Grid>
        </section>

        <section className="contentSection">
          <h2 style={{ textAlign: "center" }}>
            <span className="accentText">{t("testimony-h3-yellow")}</span>{" "}
            {t("testimony-h3-black")}
          </h2>
          <Grid container spacing={3}>
            {testimonials.map((testimonial) => (
              <Grid item xs={12} md={6} key={testimonial.nameKey}>
                <Card className="infoCard">
                  <img
                    src={`/assets/images/solar-academy/testimony-${testimonial.image}.webp`}
                    alt={t(`alt-testimony-${testimonial.image}`)}
                    className="testimonialImage"
                  />
                  <h3>{t(testimonial.nameKey)}</h3>
                  <p>{t(testimonial.quoteKey)}</p>
                </Card>
              </Grid>
            ))}
          </Grid>
        </section>

        <section className="contentSection futureSection">
          <h2>{t("future-h2")}</h2>
          <div className="ctaRow">
            <a href={FORM_URL} target="_blank" rel="noopener noreferrer">
              <CustomButton
                sx={{
                  ...ctaButtonSx,
                  my: "15px",
                  backgroundColor: "#FFFFFF",
                  color: "#333333",
                  "&:hover": {
                    backgroundColor: "#333333",
                    color: "#FFFFFF",
                  },
                }}
              >
                {t("future-cta-apply")}
              </CustomButton>
            </a>
          </div>
          <div className="futureChecks">
            {futureChecks.map((key) => (
              <div className="futureCheckItem" key={key}>
                <img
                  src="/assets/images/solar-academy/icon-checkmark.svg"
                  alt={`${brandName} check icon`}
                />
                <span>{t(key)}</span>
              </div>
            ))}
          </div>
        </section>
      </Root>
    </AppContainer>
  );
};

const Root = styled("div")(({ theme }) => ({
  marginBottom: "3rem",
  "& h2": {
    fontSize: "2rem",
  },
  "& .accentText": {
    color: theme.palette.primary.main,
  },
  "& .hero": {
    textAlign: "left",
    marginBottom: "120px",
    paddingTop: "3rem",
  },
  "& .heroTitle": {
    marginTop: 0,
  },
  // The intake line sits under the heading rather than inside it, so the h1
  // stays the programme's name and the year can change without touching it.
  "& .heroIntake": {
    margin: 0,
    fontSize: "1.25rem",
    fontWeight: 700,
    textTransform: "uppercase",
  },
  // Two equal halves side by side; stacked text-then-photo below md.
  "& .heroGrid": {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    rowGap: "2.5rem",
    [theme.breakpoints.up("md")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      columnGap: "4rem",
    },
  },
  // Side by side, the photo centres on the band from the heading down to the
  // buttons, not on the whole column: the photo carries the same top offset as
  // the logo block above the heading (the 90px logo plus its 2rem margin), and
  // both columns centre in the row, which holds whichever column is taller.
  "& .heroText, & .heroImageCol": {
    [theme.breakpoints.up("md")]: {
      alignSelf: "center",
    },
  },
  "& .heroImage": {
    width: "100%",
    borderRadius: "1rem",
    display: "block",
    [theme.breakpoints.up("md")]: {
      marginTop: "calc(90px + 2rem)",
    },
    mixBlendMode: "multiply",
    filter: "contrast(1.1)",
  },
  "& .programmeLogo": {
    maxWidth: "90px",
    width: "100%",
    height: "auto",
    margin: "0 0 2rem",
    display: "block",
  },
  "& .contentSection": {
    marginBottom: "150px",
  },
  "& .ctaRow": {
    display: "flex",
    gap: "2rem",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "flex-start",
    marginTop: "2rem",
  },
  "& .centerRow": {
    justifyContent: "center",
  },
  "& .socmedIconHolder": {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textDecoration: "none",
    transition: "transform 0.2s ease-in-out",
    "&:hover": {
      transform: "translateY(-1px)",
    },
  },
  "& .socmedIcon": {
    color: theme.palette.success.main,
    fontSize: "2.8rem",
  },
  "& .urgencyText": {
    fontWeight: 700,
    marginTop: "3rem",
  },
  "& .infoCard": {
    padding: "1rem 1.2rem",
    borderRadius: "1rem",
    boxShadow: "none",
    border: "1px solid #ededed",
    height: "100%",
  },
  // Desktop: four boxes on a 13-column grid, each spanning 4 columns and
  // starting 3 columns and one row on from the last, so they climb and overlap
  // like steps. Below md they stack and the arrows are hidden.
  "& .careerSteps": {
    listStyle: "none",
    padding: 0,
    margin: "2rem auto 1rem",
    maxWidth: "980px",
    display: "grid",
    gridTemplateColumns: "1fr",
    rowGap: ".75rem",
    [theme.breakpoints.up("md")]: {
      gridTemplateColumns: "repeat(13, minmax(0, 1fr))",
      gridTemplateRows: "repeat(4, minmax(6.5rem, auto))",
      rowGap: 0,
    },
  },
  // Text colour is set per step inline, since it depends on the box colour.
  "& .careerStep": {
    position: "relative",
    padding: "1.2rem 1.5rem",
    borderRadius: "5px",
    marginBottom: 0,
    "& h3": {
      margin: "0 0 .4rem",
      fontSize: "1.5rem",
      fontWeight: 700,
      textTransform: "uppercase",
    },
    "& p": {
      margin: 0,
      fontWeight: 700,
    },
  },
  "& .careerStep:nth-of-type(1)": {
    [theme.breakpoints.up("md")]: { gridColumn: "1 / span 4", gridRow: 4 },
  },
  "& .careerStep:nth-of-type(2)": {
    [theme.breakpoints.up("md")]: { gridColumn: "4 / span 4", gridRow: 3 },
  },
  "& .careerStep:nth-of-type(3)": {
    [theme.breakpoints.up("md")]: { gridColumn: "7 / span 4", gridRow: 2 },
  },
  "& .careerStep:nth-of-type(4)": {
    [theme.breakpoints.up("md")]: { gridColumn: "10 / span 4", gridRow: 1 },
  },
  // Rises one row from the box's top edge and lands on the next box, which
  // starts three quarters of the way along this one.
  "& .careerArrow": {
    display: "none",
    color: "#222",
    [theme.breakpoints.up("md")]: {
      display: "block",
      position: "absolute",
      bottom: "100%",
      left: "30%",
      width: "44%",
      height: "100%",
    },
  },
  "& .planPhases": {
    listStyle: "none",
    padding: 0,
    margin: "2rem auto 1rem",
    maxWidth: "980px",
    display: "grid",
    gridTemplateColumns: "1fr",
    rowGap: "2.5rem",
    [theme.breakpoints.up("md")]: {
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      columnGap: "3.5rem",
    },
  },
  // `--phase-color` is set per phase inline and drives the pin, the card
  // border and the card title.
  "& .planPhase": {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    marginBottom: 0,
  },
  // A teardrop: one corner barely rounded, rotated so it points down at the bar.
  "& .planPin": {
    width: "96px",
    height: "96px",
    margin: "0 auto 2.5rem",
    backgroundColor: "var(--phase-color)",
    borderRadius: "50% 50% 50% 5px",
    transform: "rotate(-45deg)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  "& .planPinInner": {
    width: "62px",
    height: "62px",
    borderRadius: "50%",
    backgroundColor: "#FFFFFF",
    transform: "rotate(45deg)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": {
      fontSize: "2rem",
      color: "#1A1A1A",
    },
  },
  // On desktop the three durations join into one bar that ends in an arrow;
  // below md each is its own pill above its card.
  "& .planDuration": {
    position: "relative",
    fontWeight: 700,
    fontSize: "1.1rem",
    textAlign: "center",
    padding: ".5rem 1rem",
    borderRadius: "999px",
    marginBottom: "1.5rem",
    // Negative margins of half the column gap close the gaps into one bar.
    [theme.breakpoints.up("md")]: {
      margin: "0 -1.75rem 2rem",
      borderRadius: 0,
    },
  },
  "& .planPhase:nth-of-type(1) .planDuration": {
    backgroundColor: "#FEEF94",
    [theme.breakpoints.up("md")]: {
      marginLeft: 0,
      borderRadius: "999px 0 0 999px",
    },
  },
  "& .planPhase:nth-of-type(2) .planDuration": { backgroundColor: "#FFD765" },
  "& .planPhase:nth-of-type(3) .planDuration": {
    backgroundColor: "#F9BE15",
    [theme.breakpoints.up("md")]: {
      marginRight: "2.5rem",
    },
  },
  // An SVG rather than a clip-path so the arrowhead's corners can be rounded.
  "& .planArrow": {
    display: "none",
    color: "#F9BE15",
    [theme.breakpoints.up("md")]: {
      display: "block",
      position: "absolute",
      top: "50%",
      right: "-2.5rem",
      width: "2.5rem",
      height: "4.5rem",
      transform: "translateY(-50%)",
    },
  },
  "& .planCard": {
    flexGrow: 1,
    border: "2px solid var(--phase-color)",
    borderRadius: "1rem",
    backgroundColor: "#FFFFFF",
    padding: "1.5rem 1.2rem",
    textAlign: "center",
    "& h3": {
      margin: "0 0 1rem",
      fontSize: "1.2rem",
      fontWeight: 700,
      color: "var(--phase-color)",
    },
    "& p": {
      margin: 0,
      color: "#4F4F4F",
      fontSize: "15px",
    },
  },
  "& .galleryImage": {
    width: "100%",
    borderRadius: ".5rem",
    display: "block",
    aspectRatio: "1 / 1",
    objectFit: "cover",
  },
  "& .galleryButton": {
    border: 0,
    background: "transparent",
    padding: 0,
    display: "block",
    width: "100%",
    cursor: "pointer",
  },
  "& .activityVideos": {
    marginBottom: "1rem",
  },
  "& .subjectsImage": {
    width: "100%",
    maxWidth: "520px",
    height: "520px",
    borderRadius: "1rem",
    display: "block",
    objectFit: "cover",
    margin: "0 auto",
    [theme.breakpoints.down("md")]: {
      height: "auto",
      aspectRatio: "4 / 3",
    },
  },
  "& .subjectsList": {
    marginTop: ".5rem",
  },
  "& .jobImage": {
    width: "100%",
    maxWidth: "520px",
    borderRadius: "1rem",
    display: "block",
    aspectRatio: "4 / 3",
    objectFit: "cover",
    margin: "0 auto",
  },
  "& .jobList": {
    marginTop: ".5rem",
  },
  "& .criteriaImage": {
    width: "100%",
    maxWidth: "520px",
    borderRadius: "1rem",
    display: "block",
    aspectRatio: "4 / 3",
    objectFit: "cover",
    margin: "0 auto",
  },
  "& .criteriaList": {
    marginTop: ".5rem",
  },
  "& .testimonialImage": {
    width: "100%",
    borderRadius: "1rem",
    display: "block",
    aspectRatio: "4 / 3",
    objectFit: "cover",
    marginBottom: "1rem",
  },
  "& .benefitRow": {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    "& img": {
      width: "64px",
      height: "64px",
      objectFit: "contain",
      flexShrink: 0,
    },
    "& h3": {
      margin: 0,
    },
  },
  "& .whyGridItem": {
    [theme.breakpoints.up("md")]: {
      flexBasis: "20%",
      maxWidth: "20%",
    },
  },
  "& .whyCard": {
    padding: "2rem 1rem",
    borderRadius: "0.65rem",
    boxShadow: "none",
    border: "none",
    backgroundColor: "#F8BE15",
    height: "100%",
  },
  "& .whyBenefitRow": {
    flexDirection: "column",
    justifyContent: "center",
    textAlign: "center",
    gap: "2rem",
    minHeight: "126px",
    "& h3": {
      fontSize: ".95rem",
      lineHeight: 1.35,
      margin: 0,
    },
  },
  "& .partnersLogoGrid": {
    marginTop: "1rem",
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "1.2rem 1.5rem",
    alignItems: "center",
  },
  "& .partnerLogoItem": {
    minHeight: "72px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  "& .partnerLogo": {
    maxHeight: "62px",
    maxWidth: "170px",
    width: "auto",
    height: "auto",
    display: "block",
    margin: "0 auto",
    objectFit: "contain",
    mixBlendMode: "multiply",
    filter: "contrast(1.1)",
  },
  "& ul": {
    paddingLeft: "1.5rem",
  },
  "& li": {
    marginBottom: ".4rem",
  },
  "& .futureChecks": {
    marginTop: "1.2rem",
    display: "flex",
    flexWrap: "wrap",
    gap: "1rem 2rem",
    alignItems: "center",
    justifyContent: "center",
  },
  "& .futureCheckItem": {
    display: "flex",
    gap: ".7rem",
    alignItems: "center",
    marginBottom: 0,
    "& img": {
      width: "20px",
      height: "20px",
    },
  },
  "& .futureSection": {
    backgroundColor: "#F8BE15",
    borderRadius: "1rem",
    padding: "60px 1.5rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "40px",
    marginBottom: 0,
    "& h2": {
      textAlign: "center",
      marginTop: 0,
      marginBottom: 0,
    },
    "& .ctaRow": {
      justifyContent: "center",
      marginTop: 0,
    },
    "& .futureChecks": {
      marginTop: 0,
    },
  },
  [theme.breakpoints.down("sm")]: {
    "& .ctaRow": {
      justifyContent: "flex-start",
    },
    "& .futureSection .ctaRow": {
      justifyContent: "center",
    },
  },
  [theme.breakpoints.up("sm")]: {
    "& .partnersLogoGrid": {
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    },
  },
  [theme.breakpoints.up("md")]: {
    "& .partnersLogoGrid": {
      gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
      gap: "1.8rem 2.2rem",
    },
  },
}));

const StyledLightboxModal = styled(Modal)(({ theme }) => ({
  "& .lightboxOverlay": {
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: ".8rem",
    padding: "1rem",
    boxSizing: "border-box",
  },
  "& .lightboxContent": {
    position: "relative",
    maxWidth: "min(92vw, 980px)",
    maxHeight: "92vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  "& .lightboxImage": {
    maxWidth: "100%",
    maxHeight: "92vh",
    width: "auto",
    height: "auto",
    borderRadius: ".5rem",
    display: "block",
  },
  "& .lightboxClose": {
    position: "absolute",
    top: ".5rem",
    right: ".5rem",
    zIndex: 2,
    color: "#FFFFFF",
    backgroundColor: "rgba(0,0,0,.45)",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,.65)",
    },
  },
  "& .lightboxNav": {
    color: "#FFFFFF",
    backgroundColor: "rgba(0,0,0,.35)",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,.55)",
    },
    "& svg": {
      fontSize: "2rem",
    },
  },
  [theme.breakpoints.down("sm")]: {
    "& .lightboxOverlay": {
      gap: ".3rem",
      padding: ".6rem",
    },
    "& .lightboxNav": {
      "& svg": {
        fontSize: "1.5rem",
      },
    },
  },
}));

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await cmsPageProps(locale, ["solar-academy"])),
  },
  revalidate: 60,
});

export default SolarAcademyNativePage;
